import useSWR from "swr";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { privateOfficeService } from "@/services/private-office-service";
import type { SpaceComplianceDocument } from "@/services/private-office-service/types";
import { useAuth } from "@/features/authentication";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";

type DocumentType = "avcb" | "permit";

const DOCUMENT_LABEL: Record<DocumentType, string> = {
  avcb: "AVCB",
  permit: "Alvará de funcionamento",
};

/** `yyyy-MM-dd` → `dd/MM/yyyy`, by the calendar day — no Date parsing, so the
 * day never shifts with the browser's timezone. */
function formatDay(day: string): string {
  const [y, m, d] = day.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

/** Status line of one document. Only two states exist in the inventory: on
 * record or not. "Não informado" never means the building lacks it. */
export function describeComplianceDocument(
  document: SpaceComplianceDocument | undefined,
): { text: string; tone: "ok" | "warning" | "muted" } {
  if (!document?.url) return { text: "Não informado", tone: "muted" };
  // `valid` is the backend's verdict (same rule as the filter and the client
  // badge) — never contradict it here.
  if (!document.valid) {
    return document.expiry_date
      ? {
          text: `Vencido em ${formatDay(document.expiry_date)}`,
          tone: "warning",
        }
      : { text: "Data de validade ilegível", tone: "warning" };
  }
  return document.expiry_date
    ? {
        text: `Possui · válido até ${formatDay(document.expiry_date)}`,
        tone: "ok",
      }
    : { text: "Possui", tone: "ok" };
}

/** Only http(s) links are rendered: the URL comes from inventory data, and
 * React does not block `javascript:` hrefs. */
export function safeDocumentUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:" ? url : null;
  } catch {
    return null;
  }
}

const TONE_COLOR = {
  ok: "#3F6B14",
  warning: "#B45309",
  muted: "#9E9A94",
} as const;

/**
 * AVCB and alvará of the room's building, for the sales executive while
 * building the proposal (PED-14095). The file URLs come from a dedicated
 * endpoint fetched here, on demand — they never travel with the search
 * results, because those are copied into the client-facing proposal.
 */
export function SpaceComplianceDocuments({
  spaceId,
  roomId,
}: {
  spaceId: number | undefined;
  roomId: string;
}) {
  const { userInfo } = useAuth();
  const { data, error, isLoading } = useSWR(
    spaceId ? ["space-compliance-documents", spaceId] : null,
    () => privateOfficeService.getSpaceComplianceDocuments(spaceId!),
    { revalidateOnFocus: false },
  );

  if (!spaceId) return null;

  const trackView = (documentType: DocumentType) => {
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_COMPLIANCE_DOCUMENT_VIEWED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        document_type: documentType,
        space_id: spaceId,
        room_id: roomId,
      },
    });
  };

  return (
    <div className="px-6 pb-6" data-testid="space-compliance-documents">
      <h4
        className="text-sm font-semibold mb-1 flex items-center gap-1.5"
        style={{ color: "#1A1814" }}
      >
        <ShieldCheck className="w-4 h-4" />
        Documentos do espaço
      </h4>
      <p className="text-xs mb-3" style={{ color: "#9E9A94" }}>
        Documentos do prédio onde a sala fica. O cliente vê só o selo, sem
        acesso ao arquivo.
      </p>
      {isLoading ? (
        <span className="text-xs" style={{ color: "#9E9A94" }}>
          Carregando…
        </span>
      ) : error ? (
        <span className="text-xs" style={{ color: "#9E9A94" }}>
          Não foi possível carregar os documentos.
        </span>
      ) : (
        <div className="flex flex-col gap-2">
          {(["avcb", "permit"] as const).map((type) => {
            const document = data?.[type];
            const status = describeComplianceDocument(document);
            const href = safeDocumentUrl(document?.url);
            return (
              <div
                key={type}
                className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                style={{ background: "#F7F5F2" }}
              >
                <div className="flex flex-col">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: "#43403D" }}
                  >
                    {DOCUMENT_LABEL[type]}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: TONE_COLOR[status.tone] }}
                  >
                    {status.text}
                  </span>
                </div>
                {href && (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackView(type)}
                    onAuxClick={() => trackView(type)}
                    className="inline-flex items-center gap-1 text-xs font-semibold shrink-0 hover:opacity-80"
                    style={{ color: "#1A1814" }}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Ver documento
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
